/**
 * Error Boundary Component for THE ONESCENT DIAL
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them, and displays a fallback UI instead of crashing.
 */

import React from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Send error telemetry to backend for remote debugging
    try {
      const errorReport = {
        error: error?.toString?.() || 'Unknown error',
        componentStack: errorInfo?.componentStack?.substring(0, 500) || '',
        userAgent: navigator.userAgent,
        deviceMemory: navigator.deviceMemory || 'unknown',
        hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
        screenSize: `${window.screen?.width}x${window.screen?.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString()
      };
      fetch('/api/error-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorReport)
      }).catch(() => {}); // Fire-and-forget
    } catch (e) {
      // Telemetry failure should never cause additional problems
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div 
          className="min-h-screen bg-tod-bg-secondary flex items-center justify-center p-4"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-md w-full bg-tod-bg-primary border border-tod-brass/30 p-8 text-center">
            {/* Icon */}
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-tod-brass/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-tod-brass" />
            </div>
            
            {/* Title */}
            <h1 
              className="text-xl font-bold text-tod-brass mb-2 uppercase tracking-wider"
              style={{ fontFamily: 'Cinzel Decorative, serif' }}
            >
              Temporal Disruption
            </h1>
            
            {/* Message */}
            <p className="text-tod-text-secondary mb-6 text-sm">
              The celestial calculations encountered an unexpected condition. 
              This moment has been logged for our astronomers to review.
            </p>
            
            {/* Error details (accessible via details toggle for user debugging) */}
            {this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-tod-accent-purple text-xs cursor-pointer hover:text-tod-accent-pink">
                  Technical Details (share with support)
                </summary>
                <pre className="mt-2 p-3 bg-tod-bg-secondary border border-tod-accent-purple/20 text-tod-error text-xs overflow-auto max-h-32 select-all">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            
            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="flex items-center justify-center gap-2 px-6 py-2 bg-tod-brass text-tod-bg-secondary font-bold uppercase tracking-wider text-sm hover:bg-tod-text-primary transition-colors"
                data-testid="error-retry-btn"
              >
                <RefreshCw size={16} />
                Try Again
              </button>
              
              <button
                onClick={this.handleRefresh}
                className="flex items-center justify-center gap-2 px-6 py-2 border border-tod-brass/50 text-tod-brass font-bold uppercase tracking-wider text-sm hover:bg-tod-brass/10 transition-colors"
                data-testid="error-refresh-btn"
              >
                Refresh Page
              </button>
            </div>
            
            {/* Footer note */}
            <p className="mt-6 text-tod-text-secondary text-xs">
              Cycles in Harmony describes conditions, not commands.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
